import React from 'react';
import {
  ColumnActionsMode,
  IColumn,
  ITooltipHostStyles,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import { CheckmarkCircleColor } from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { EntityTerminationUriSegment, IEntityTerminationActionItem } from '../types';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';

export type EntityTerminationStageSegment =
  | EntityTerminationUriSegment.FinanceIncomplete
  | EntityTerminationUriSegment.LegalInProcess;

const tooltipHostStyles: Partial<ITooltipHostStyles> = {
  root: { textOverflow: 'ellipsis', overflow: 'hidden' },
};

const onRenderActionItemName = (
  item: IEntityTerminationActionItem,
  companyCode: string,
  stage: EntityTerminationStageSegment,
): JSX.Element => {
  return (
    <TooltipHost content={item?.description} id="tooltip">
      <Link
        to={{
          pathname: `/monitoring-queue/${companyCode}/${stage}/actionItem/${item.id}`,
          state: {
            selectedActionItem: item,
          },
        }}
      >
        {item?.description}
      </Link>
    </TooltipHost>
  );
};

const onRenderCompletedStatus = (item: IEntityTerminationActionItem): JSX.Element => {
  const hasDateCompleted = !!item?.dateCompleted;

  return <>{hasDateCompleted && <CheckmarkCircleColor fontSize={20} />}</>;
};

const onRenderLatestComment = (item: IEntityTerminationActionItem): JSX.Element => {
  if (!item?.status || item?.status.trim() === '') {
    return <></>;
  }

  return (
    <TooltipHost
      delay={TooltipDelay.medium}
      key={item.id}
      styles={tooltipHostStyles}
      tooltipProps={{
        onRenderContent: () => {
          return <span>{item?.status}</span>;
        },
      }}
    >
      {item?.status ? <span>{item?.status}</span> : ''}
    </TooltipHost>
  );
};

const onRenderOwner = (item: IEntityTerminationActionItem): JSX.Element => {
  return (
    <TooltipHost
      delay={TooltipDelay.medium}
      key={item.id}
      styles={tooltipHostStyles}
      tooltipProps={{
        onRenderContent: () => {
          return <span>{item.owner?.text}</span>;
        },
      }}
    >
      {' '}
      {item.owner?.text}
    </TooltipHost>
  );
};

const getColumns = (companyCode: string, stage: EntityTerminationStageSegment): Array<IColumn> => [
  {
    key: 'completedStatus',
    name: 'Completion Status',
    fieldName: 'completedStatus',
    minWidth: 30,
    maxWidth: 30,
    isResizable: false,
    onRender: (item) => onRenderCompletedStatus(item),
  },
  {
    key: 'description',
    name: 'Action Item',
    fieldName: 'description',
    minWidth: 200,
    maxWidth: 200,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderActionItemName(item, companyCode, stage),
  },
  {
    key: 'actionOwner',
    name: 'Action Owner',
    fieldName: 'actionOwner',
    minWidth: 200,
    maxWidth: 200,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderOwner(item),
  },
  {
    key: 'status',
    name: 'Status',
    fieldName: 'status',
    minWidth: 400,
    maxWidth: 400,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderLatestComment(item),
  },
  {
    key: 'dateStarted',
    name: 'Date Started',
    fieldName: 'dateStarted',
    minWidth: 100,
    maxWidth: 100,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const dateStarted = momentUtc(item.dateStarted, StatDateFormats.TwoDigitDate, true);
      return <span>{dateStarted}</span>;
    },
  },
  {
    key: 'dateCompleted',
    name: 'Date Completed',
    fieldName: 'dateCompleted',
    minWidth: 100,
    maxWidth: 100,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const dateCompleted = momentUtc(item.dateCompleted, StatDateFormats.TwoDigitDate, true);
      return <span>{dateCompleted}</span>;
    },
  },
];

export default getColumns;
